import { useMemo } from 'react'

import { Box, Spinner, Text } from '@factorialco/deprecated-design-system'
import { addDays, endOfISOWeek, format, isAfter, parseISO, startOfISOWeek } from 'date-fns'
import { EngagementEmployeePulseSummaryFilter } from 'generated/resources/datatable.graphql'
import { PulseChartDocument } from 'generated/resources/graphql'

import BarChart from 'modules/core/components/BarChart'
import useFilters from 'modules/core/components/DataTable/hooks/useFilters'
import { useQuery } from 'modules/core/hooks/useQuery'

import { ChartSkeleton } from 'modules/engagement/components/ChartSkeleton'

interface Prop {
  startDate: Date
  endDate: Date
}

const formatDate = (value: string) => {
  const date = parseISO(value)
  const dayInitial =
    format(date, 'EEE').charAt(0).toUpperCase() + format(date, 'EEE').slice(1).toLowerCase()
  const dayOfMonth = format(date, 'd')
  return `${dayInitial} ${dayOfMonth}`
}

const Chart = ({ startDate, endDate }: Prop) => {
  const { getFiltersVariables } = useFilters<EngagementEmployeePulseSummaryFilter>()
  const filters: EngagementEmployeePulseSummaryFilter = getFiltersVariables().filter || {}

  const { data: chartData, loading: isChartLoading } = useQuery(PulseChartDocument, {
    variables: {
      from: format(startOfISOWeek(startDate), 'yyyy-MM-dd'),
      to: format(endOfISOWeek(endDate), 'yyyy-MM-dd'),
      managerId: filters?.managerId?.in || [],
      values: filters?.value?.in || [],
      teamId: filters?.teamId?.in || [],
      tags: filters?.tag?.in || [],
    },
  })

  const processedChartData = useMemo(() => {
    const weekStart = startOfISOWeek(startDate)
    const weekEnd = endOfISOWeek(endDate)

    const allDays = []
    let current = weekStart

    while (!isAfter(current, weekEnd)) {
      allDays.push(format(current, 'yyyy-MM-dd'))
      current = addDays(current, 1)
    }

    const apiData = new Map()
    if (chartData?.engagement?.employeePulseSummariesConnection?.aggregate?.terms?.submittedDate) {
      const terms =
        chartData.engagement.employeePulseSummariesConnection.aggregate.terms.submittedDate
      terms.values?.forEach((value: string, index: number) => {
        apiData.set(value, terms.count?.[index] || 0)
      })
    }

    return allDays.map((day) => ({
      bucket: formatDate(day),
      value: apiData.get(day) || 0,
    }))
  }, [chartData, endDate, startDate])

  const maxChartValue = useMemo(() => {
    if (processedChartData.length === 0) return 10
    const maxValue = Math.max(...processedChartData.map((item) => item.value))
    return maxValue > 0 ? maxValue : 10
  }, [processedChartData])

  const isEmpty =
    !chartData?.engagement.employeePulseSummariesConnection.aggregate?.terms?.submittedDate?.count?.reduce(
      (acc, n) => n + acc,
      0
    )

  const hasRecords = !!chartData?.engagement.employeePulseSummariesConnection.totalCount

  return (
    <>
      {isChartLoading ? (
        <Box width="full" alignItems="center" justifyContent="center" height="full">
          <Spinner />
        </Box>
      ) : (
        <>
          <Text.Normal>
            {chartData?.engagement?.employeePulseSummariesConnection?.aggregate?.count} responses
          </Text.Normal>
          {isEmpty || !hasRecords ? (
            <ChartSkeleton hasRecords={hasRecords} />
          ) : (
            <BarChart
              margin={{ top: 30, bottom: 80, left: 35, right: 0 }}
              colors={['viridian1000']}
              colorBy="indexValue"
              groupMode="grouped"
              keys={['value']}
              indexBy="bucket"
              maxValue={maxChartValue}
              enabledGridY
              data={processedChartData}
              selectableBars={false}
            />
          )}
        </>
      )}
    </>
  )
}

export default Chart
